import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2,
  Menu,
  X,
  ChevronDown,
  Globe,
  Code2,
  Bot,
  PieChart,
  Database,
  Workflow,
  ArrowRight,
  Plug,
} from "lucide-react";
import logoSfundo from "@/assets/logo_sfundo.png";

type NavItem = { label: string; id: string; hasSubmenu?: boolean };

const NAV_ITEMS: NavItem[] = [
  { label: "Início", id: "hero" },
  { label: "Recursos", id: "recursos" },
  { label: "Ponto Digital", id: "ponto-digital" },
  { label: "Serviços", id: "servicos", hasSubmenu: true },
  { label: "Diferenciais", id: "diferenciais" },
  { label: "Clientes", id: "depoimentos" },
  { label: "Contato", id: "contato" },
];

const SERVICE_MENU = [
  { label: "Gestão de Condomínios", href: "/servicos/gestao-condominios", icon: Building2, tone: "from-primary to-blue-500" },
  { label: "Webdesign & Landing Pages", href: "/servicos/webdesign", icon: Globe, tone: "from-cyan-400 to-blue-500" },
  { label: "Sistemas Web (SaaS/ERP/CRM)", href: "/servicos/sistemas-web", icon: Code2, tone: "from-violet-500 to-fuchsia-500" },
  { label: "Chatbots com IA", href: "/servicos/chatbots", icon: Bot, tone: "from-emerald-400 to-teal-500" },
  { label: "Power BI & Dashboards", href: "/servicos/power-bi", icon: PieChart, tone: "from-amber-400 to-orange-500" },
  { label: "ETL Java + Python", href: "/servicos/etl", icon: Database, tone: "from-sky-400 to-indigo-500" },
  { label: "Automação de Processos", href: "/servicos/automacao", icon: Workflow, tone: "from-pink-500 to-rose-500" },
  { label: "Integrações por API", href: "/servicos/integracoes", icon: Plug, tone: "from-blue-600 to-indigo-600" },
];

/**
 * Navbar pública compartilhada entre a Landing e as páginas de serviço.
 * Mantém o mesmo visual (slate-900 / blur / cantos arredondados).
 * Os links de seção navegam para a Landing (`/#id`).
 */
export const PublicNavbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const navigate = useNavigate();

  const goToSection = (id: string) => {
    setIsMenuOpen(false);
    navigate(`/#${id}`);
    // Após a navegação, tenta scroll se já estamos na home
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  return (
    <header className="fixed top-4 left-4 right-4 z-50 bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-2xl">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex items-center">
            <img 
              src={logoSfundo} 
              alt="itaCondo" 
              style={{ height: "45px", width: "auto", display: "block" }} 
            />
          </Link>

          <nav className="hidden lg:flex items-center space-x-8">
            {NAV_ITEMS.map((item) =>
              item.hasSubmenu ? (
                <div key={item.id} className="group relative">
                  <button
                    onClick={() => goToSection(item.id)}
                    className="flex items-center gap-1 text-slate-300 hover:text-white transition-colors text-sm font-medium"
                  >
                    {item.label}
                    <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
                  </button>
                  <div className="invisible absolute left-1/2 top-full z-50 mt-3 w-[340px] -translate-x-1/2 translate-y-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                    <div className="rounded-2xl border border-white/10 bg-slate-900/95 p-2 shadow-2xl backdrop-blur-xl">
                      {SERVICE_MENU.map((s) => {
                        const Icon = s.icon;
                        return (
                          <Link
                            key={s.href}
                            to={s.href}
                            className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-slate-200 transition hover:bg-white/5 hover:text-white"
                          >
                            <span className={`grid size-9 shrink-0 place-items-center rounded-lg bg-gradient-to-br ${s.tone} text-white shadow-md`}>
                              <Icon className="h-4 w-4" />
                            </span>
                            <span className="min-w-0 flex-1 truncate font-medium">{s.label}</span>
                            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  key={item.id}
                  onClick={() => goToSection(item.id)}
                  className="text-slate-300 hover:text-white transition-colors text-sm font-medium"
                >
                  {item.label}
                </button>
              ),
            )}
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <Link 
                to="/login" 
                style={{ fontSize: "14.5px", color: "var(--muted)", fontWeight: 600, cursor: "pointer", transition: "color .2s" }}
                className="hover:text-white"
              >
                Entrar
              </Link>
              <div 
                onClick={() => goToSection("contato")}
                className="btn-hover"
                style={{ 
                  position: "relative", 
                  overflow: "hidden", 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "8px", 
                  padding: "11px 20px", 
                  borderRadius: "11px", 
                  background: "var(--blue)", 
                  color: "#fff", 
                  fontWeight: 700, 
                  fontSize: "14px", 
                  cursor: "pointer", 
                  boxShadow: "0 8px 22px rgba(59,123,255,.38)", 
                  transition: "transform .2s,box-shadow .2s" 
                }}
              >
                <span style={{ position: "absolute", top: 0, left: 0, width: "40%", height: "100%", background: "linear-gradient(105deg,transparent,rgba(255,255,255,.45),transparent)", animation: "shimmer 4s infinite" }}></span>
                Agendar demonstração
              </div>
            </div>
          </nav>

          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Abrir menu"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-white/10 animate-fade-in">
            <div className="flex flex-col space-y-3">
              {NAV_ITEMS.map((item) =>
                item.hasSubmenu ? (
                  <div key={item.id} className="flex flex-col">
                    <button
                      onClick={() => setIsMobileServicesOpen((v) => !v)}
                      className="flex items-center justify-between text-slate-300 hover:text-white transition-colors text-left py-2"
                    >
                      <span>{item.label}</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${isMobileServicesOpen ? "rotate-180" : ""}`} />
                    </button>
                    {isMobileServicesOpen && (
                      <div className="mt-1 ml-2 flex flex-col gap-1 border-l border-white/10 pl-3 animate-fade-in">
                        {SERVICE_MENU.map((s) => {
                          const Icon = s.icon;
                          return (
                            <Link
                              key={s.href}
                              to={s.href}
                              onClick={() => setIsMenuOpen(false)}
                              className="flex items-center gap-2.5 rounded-lg px-2 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-white"
                            >
                              <span className={`grid size-7 shrink-0 place-items-center rounded-md bg-gradient-to-br ${s.tone} text-white`}>
                                <Icon className="h-3.5 w-3.5" />
                              </span>
                              <span className="truncate">{s.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => goToSection(item.id)}
                    className="text-slate-300 hover:text-white transition-colors text-left py-2"
                  >
                    {item.label}
                  </button>
                ),
              )}
              <div className="flex flex-col gap-3 pt-2">
                <Link 
                  to="/login" 
                  onClick={() => setIsMenuOpen(false)}
                  style={{ fontSize: "14.5px", color: "var(--muted)", fontWeight: 600, cursor: "pointer", textAlign: "center", padding: "10px 0", transition: "color .2s" }}
                  className="hover:text-white"
                >
                  Entrar
                </Link>
                <div 
                  onClick={() => { goToSection("contato"); setIsMenuOpen(false); }}
                  className="btn-hover"
                  style={{ 
                    position: "relative", 
                    overflow: "hidden", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    gap: "8px", 
                    padding: "12px 20px", 
                    borderRadius: "11px", 
                    background: "var(--blue)", 
                    color: "#fff", 
                    fontWeight: 700, 
                    fontSize: "14px", 
                    cursor: "pointer", 
                    boxShadow: "0 8px 22px rgba(59,123,255,.38)", 
                    transition: "transform .2s,box-shadow .2s" 
                  }}
                >
                  <span style={{ position: "absolute", top: 0, left: 0, width: "40%", height: "100%", background: "linear-gradient(105deg,transparent,rgba(255,255,255,.45),transparent)", animation: "shimmer 4s infinite" }}></span>
                  Agendar demonstração
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default PublicNavbar;